import React, { useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup, ZoomControl } from "react-leaflet";
import L from "leaflet";
import { MapPin, Truck, Maximize2 } from "lucide-react";
import { CITIES_GEO } from "@/data/citiesGeo";

// Auto-fit map to all cities once it's mounted
function FitToCities({ map }) {
  useEffect(() => {
    if (!map) return;
    const bounds = L.latLngBounds(CITIES_GEO.map((c) => [c.lat, c.lon]));
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map]);
  return null;
}

export default function CoverageMap() {
  const [hovered, setHovered] = useState(null);
  const [map, setMap] = useState(null);

  return (
    <section
      id="obszar"
      data-testid="coverage-section"
      className="relative bg-zinc-950 text-white py-20 md:py-28 overflow-hidden"
    >
      {/* subtle blueprint accent */}
      <div className="absolute inset-0 blueprint-grid opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[var(--feniks-red)] mb-3">
              [04] Obszar działania
            </div>
            <h2
              data-testid="coverage-headline"
              className="font-display text-3xl md:text-4xl lg:text-5xl leading-none tracking-tighter mb-5"
            >
              Dojeżdżamy w 28 miastach Śląska i Zagłębia.
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              Centrum naszego działania to{" "}
              <span className="text-white font-semibold">Katowice</span>. Dojeżdżamy do klientów
              w całej aglomeracji śląskiej i Zagłębiu Dąbrowskim. Nie ma Twojego miasta?
              Zadzwoń — możemy umówić wizytę.
            </p>
            <div className="inline-flex items-center gap-3 border border-zinc-800 px-4 py-3 bg-zinc-900/60">
              <Truck size={18} className="text-[var(--feniks-red)]" />
              <span className="text-sm">
                Dojazd:{" "}
                <span className="font-mono font-semibold">150 zł</span> Katowice ·{" "}
                <span className="font-mono font-semibold">200 zł</span> poza Katowicami
              </span>
            </div>
          </div>

          {/* Map */}
          <div className="lg:col-span-7">
            <div
              data-testid="coverage-map"
              className="relative h-[360px] sm:h-[440px] lg:h-[500px] border border-zinc-800 bg-zinc-900 overflow-hidden"
            >
              <MapContainer
                ref={setMap}
                center={[50.2649, 19.0238]}
                zoom={10}
                scrollWheelZoom={false}
                zoomControl={false}
                attributionControl={false}
                style={{ width: "100%", height: "100%", background: "#0a0a0a" }}
              >
                <FitToCities map={map} />
                <ZoomControl position="bottomright" />
                {/* Dark, minimalistic tiles - Carto Dark Matter (no API key needed) */}
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  subdomains={["a", "b", "c", "d"]}
                  attribution=""
                  maxZoom={19}
                />

                {/* Service area soft halo around Katowice */}
                <CircleMarker
                  center={[50.2649, 19.0238]}
                  radius={140}
                  pathOptions={{
                    color: "#E63946",
                    weight: 1,
                    opacity: 0.35,
                    fillColor: "#E63946",
                    fillOpacity: 0.05,
                    dashArray: "4 6",
                  }}
                  interactive={false}
                />

                {/* City pins */}
                {CITIES_GEO.map((c, idx) => (
                  <CircleMarker
                    key={c.name}
                    center={[c.lat, c.lon]}
                    radius={c.primary ? 8 : 5}
                    pathOptions={{
                      color: "#FFFFFF",
                      weight: c.primary ? 2 : 1.5,
                      fillColor: c.primary ? "#E63946" : "#E63946",
                      fillOpacity: c.primary ? 1 : 0.85,
                    }}
                    eventHandlers={{
                      mouseover: () => setHovered(c.name),
                      mouseout: () => setHovered(null),
                    }}
                  >
                    <Tooltip
                      direction="top"
                      offset={[0, -6]}
                      opacity={1}
                      permanent={c.primary}
                      className="feniks-leaflet-tooltip"
                    >
                      {c.name}
                    </Tooltip>
                    <Popup>
                      <div style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
                        <strong>{c.name}</strong>
                        <br />
                        <span style={{ color: "#52525B", fontSize: 12 }}>
                          {c.primary ? "Siedziba serwisu" : "Obsługujemy"}
                        </span>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>

              {/* corner overlays */}
              <div className="pointer-events-none absolute top-3 left-3 z-[400] bg-black/70 backdrop-blur-sm border border-zinc-800 px-2.5 py-1.5 font-mono text-[10px] tracking-widest text-zinc-300 uppercase">
                Śląsk · Zagłębie
              </div>
              <div className="pointer-events-none absolute bottom-3 left-3 z-[400] font-mono text-[10px] text-zinc-400 flex items-center gap-2 bg-black/70 backdrop-blur-sm px-2.5 py-1.5">
                <MapPin size={12} /> 28 miejscowości · centrum: Katowice
              </div>
              {hovered && (
                <div className="pointer-events-none absolute top-3 right-3 z-[400] bg-[var(--feniks-red)] text-white px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase">
                  ▸ {hovered}
                </div>
              )}
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-zinc-500">
              <span>© OpenStreetMap · CARTO</span>
              <span className="flex items-center gap-1.5">
                <Maximize2 size={10} /> Klikaj miasta · przewijaj / przybliżaj
              </span>
            </div>
          </div>
        </div>

        {/* City list */}
        <div data-testid="coverage-city-list" className="border-t border-zinc-800 pt-8">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500 mb-4">
            Lista obsługiwanych miejscowości
          </div>
          <div className="flex flex-wrap gap-2">
            {CITIES_GEO.map((c) => (
              <span
                key={c.name}
                onMouseEnter={() => setHovered(c.name)}
                onMouseLeave={() => setHovered(null)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border transition-colors ${
                  c.primary
                    ? "border-[var(--feniks-red)] text-white bg-[var(--feniks-red)]/15"
                    : "border-zinc-800 text-zinc-300 hover:border-zinc-600 hover:text-white"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    c.primary ? "bg-[var(--feniks-red)]" : "bg-zinc-500"
                  }`}
                />
                {c.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
